<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import InspectorTabsMenu from '@/components/DataTable/InspectorTabsMenu.vue';
import TrackCardList from '@/components/TrackCardList.vue';
import { useGeneratedPlaylistTracks } from '@/composables/api/useGeneratedPlaylistApi';
import type { TrackListExportSource } from '@/types/trackListExport';

/**
 * Inspector for a generated (zp) playlist — the daily playlists players report
 * as `<28 digits>--<name>.txt` filenames. Not a searchable entity: it's opened
 * from surfaces that resolved a playlist name (e.g. the player currently-playing
 * card) with the zone + zp id the resolver returned. Details tab shows the
 * playlist facts; Tracks renders the resolved tracks through the shared
 * TrackCard list.
 */
const props = defineProps<{
    locationZoneId: number;
    generatedPlaylistId: number;
    /** The reported filename, shown while the tracks query loads. */
    fileName?: string;
}>();

const tracksQuery = useGeneratedPlaylistTracks(
    () => props.locationZoneId,
    () => props.generatedPlaylistId
);

const list = computed(() => tracksQuery.data.value?.list ?? null);
const tracks = computed(() => tracksQuery.data.value?.data ?? []);
const fileName = computed(() => list.value?.file_name ?? props.fileName ?? '');

/** Identifies this generated playlist to the export endpoint. */
const exportSource = computed<TrackListExportSource>(() => ({
    type: 'generated_playlist',
    location_zone_id: props.locationZoneId,
    generated_playlist_id: props.generatedPlaylistId,
}));

const exportLabel = computed(
    () => `Generated Playlist - ${fileName.value || props.generatedPlaylistId}`
);

const playDate = computed(() => {
    const playDateTime = list.value?.play_date_time;
    return playDateTime ? new Date(playDateTime).toLocaleString() : null;
});

const KNOWN_TABS = ['details', 'tracks'] as const;
type InspectorTab = (typeof KNOWN_TABS)[number];
const TAB_DEFAULT: InspectorTab = 'details';
const TAB_STORAGE_KEY = 'generated-playlist-inspector:tab:v1';

function readPersistedTab(): InspectorTab {
    const stored = localStorage.getItem(TAB_STORAGE_KEY);
    return KNOWN_TABS.includes(stored as InspectorTab) ? (stored as InspectorTab) : TAB_DEFAULT;
}

const inspectorTab = ref<InspectorTab>(readPersistedTab());
watch(inspectorTab, (tab) => localStorage.setItem(TAB_STORAGE_KEY, tab));

function onTabChange(value: string | number) {
    if (KNOWN_TABS.includes(value as InspectorTab)) {
        inspectorTab.value = value as InspectorTab;
    }
}
</script>

<template>
    <Tabs
        :model-value="inspectorTab"
        class="flex min-h-0 flex-1 flex-col gap-4"
        data-testid="generated-playlist-inspector"
        @update:model-value="onTabChange"
    >
        <InspectorTabsMenu
            :model-value="inspectorTab"
            :items="[
                { value: 'details', label: 'Details' },
                { value: 'tracks', label: 'Tracks' },
            ]"
            @update:model-value="onTabChange"
        />
        <TabsContent value="details">
            <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
                <dt class="text-muted-foreground">Type</dt>
                <dd>Generated Playlist</dd>
                <dt class="text-muted-foreground">File Name</dt>
                <dd class="min-w-0 break-all" :title="fileName">{{ fileName }}</dd>
                <dt class="text-muted-foreground">Play Date</dt>
                <dd>{{ playDate ?? '—' }}</dd>
                <dt class="text-muted-foreground">Location Zone ID</dt>
                <dd class="tabular-nums">{{ locationZoneId }}</dd>
                <dt class="text-muted-foreground">Tracks</dt>
                <dd class="tabular-nums">{{ tracks.length }}</dd>
            </dl>
        </TabsContent>
        <TabsContent value="tracks">
            <TrackCardList
                :tracks="tracks"
                :is-loading="tracksQuery.isLoading.value"
                :has-error="Boolean(tracksQuery.error.value)"
                empty-text="This generated playlist has no tracks right now."
                :export-source="exportSource"
                :export-label="exportLabel"
            />
        </TabsContent>
    </Tabs>
</template>
